import { atom, constSelector, DefaultValue, selector, useRecoilValue } from 'recoil';
import { ApiClient, createApiClient } from '../@sprinx/react-after-razzle';
import { ApplicationAuthState, GlobalStateRegister } from '../@sprinx/react-after-razzle/stateStore';
import { SupportedLocale } from '../i18n/types';
import { anonymouseUserId, locale, pricesType, region, supportedCurrencies, supportedLocales } from '../config';

// export const currencyState = GlobalStateRegister.register(
//   atom<string>({
//     key: 'currency',
//     default: appSettings.currency,
//   }),
// );

export const customerRegionState = selector<string>({
  key: 'customerRegionState',
  get: ({ get }) => {
    const auth = get(authState);

    return auth?.profile?.customer.country || 'CZ';
  },
});

export const customerIdState = selector<string>({
  key: 'customerIdState',
  get: ({ get }) => {
    const auth = get(authState);
    return auth?.profile?.customer.customerId;
  },
});

export const currencyState = selector<string>({
  key: 'currencyState',
  get: ({ get }) => {
    const auth = get(authState);

    return auth?.profile?.customer.currency || 'CZK';
  },
});

export const paymentTypeLevelState = selector<number | null>({
  key: 'paymentTypeLevelState',
  get: ({ get }) => {
    const auth = get(authState);
    const paymentTypeLevel = auth?.profile?.customer.extra.paymentTypeLevel;
    return typeof paymentTypeLevel === 'number' ? paymentTypeLevel : null;
  },
});

export const decimalPrecisionState = selector<number>({
  key: 'decimalPrecisionState',
  get: ({ get }) => {
    const currency = get(currencyState);

    return currency === 'EUR' ? 4 : 2;
  },
});

export const supportedCurrenciesState = constSelector<string[]>(supportedCurrencies);

export const localeState = GlobalStateRegister.register(
  atom<SupportedLocale>({
    key: 'locale',
    default: locale,
  }),
);

export const supportedLocaleCodesState = constSelector<string[]>(supportedLocales);

export const pricesTypeState = GlobalStateRegister.register(
  atom<'B2B' | 'B2C' | 'B2BForeign' | undefined>({
    key: 'pricesType',
    default: pricesType || 'B2C',
  }),
);

export const regionState = GlobalStateRegister.register(
  atom<'_default' | string>({
    key: 'region',
    default: region || '_default',
  }),
);

export const anonymouseUserIdState = GlobalStateRegister.register(
  atom<string | undefined>({
    key: 'anonymouseUserId',
    default: anonymouseUserId || 'anonymous',
  }),
);

export const authState = GlobalStateRegister.register(
  atom<ApplicationAuthState<any> | undefined>({
    key: 'auth',
    default: undefined,
  }),
);

export const isSSRState = GlobalStateRegister.register(
  atom<boolean>({
    key: 'isSSR',
    default: false,
  }),
);

export const isAuthenticatedState = selector<boolean>({
  key: 'isAuthenticated',
  get: ({ get }) => {
    const auth = get(authState);
    return Boolean(auth && auth.user && auth.isAuthenticated);
  },
});

export const showPricesState = selector<boolean>({
  key: 'showPrices',
  get: ({ get }) => {
    const auth = get(authState);
    return Boolean(auth && auth.profile && auth.profile.showPrices);
  },
});

export const isMasterScopeState = selector<boolean>({
  key: 'isMasterScope',
  get: ({ get }) => {
    const auth = get(authState);
    return Boolean(auth && auth.user && (auth.scope || []).includes('vrp:master'));
  },
});

export const authUserState = selector<ApplicationAuthState<any>['user']>({
  key: 'authUser',
  get: ({ get }) => {
    const auth = get(authState);
    return auth?.user;
  },
});

export const apiClientState = selector({
  key: 'apiClient',
  get: ({ get }) => {
    const auth = get(authState);
    return createApiClient({ accessToken: auth?.accessToken });
  },
});

export const getloyaltyPointsState = (
  apiClient: ApiClient,
): Promise<{
  invoicesStats: { isEnough: boolean; totalValueSpent: number; totalValueSpentTarget: number };
  loyaltyPoints: number;
}> => {
  return apiClient.get('/v1/loyaltyTransactions/getLoyaltyPoints', {});
};

export const getSummerPromoCall = (
  apiClient: ApiClient,
): Promise<{
  loyaltyPointsThisYear: number;
  totalValueSpentLastYear: number;
  totalValueSpentThisYear: number;
}> => {
  return apiClient.get<
    {
      loyaltyPointsThisYear: number;
      totalValueSpentLastYear: number;
      totalValueSpentThisYear: number;
    },
    {}
  >('/v1/loyaltyTransactions/getSummerPromo', {});
};

export const summerPromoState = selector<{
  loyaltyPointsThisYear: number;
  totalValueSpentLastYear: number;
  totalValueSpentThisYear: number;
}>({
  key: 'summerPromo',
  get: ({ get }) => {
    const initialState = get(summerPromoInitialState);
    if (initialState) {
      return initialState;
    }
    const apiClient = get(apiClientState);
    get(summerPromoReloadTrigger);

    return getSummerPromoCall(apiClient);
  },
});

export const summerPromoInitialState = GlobalStateRegister.register(
  atom<
    | {
        loyaltyPointsThisYear: number;
        totalValueSpentLastYear: number;
        totalValueSpentThisYear: number;
      }
    | undefined
  >({
    key: 'summerPromoInitialState',
    default: undefined, // createListDefaultResult<documentsProduct>(),
  }),
);

export const summerPromoReloadTrigger = atom<number>({
  key: 'summerPromoReloadTrigger',
  default: 0,
});

export const showLoyaltyProgramState = selector<boolean>({
  key: 'showLoyaltyProgram',
  get: ({ get }) => {
    const auth = get(authState);
    return Boolean(auth && auth.profile && auth.profile.showLoyaltyProgram);
  },
});

export const loyaltyPointsState = selector<number>({
  key: 'loyaltyPoints',
  get: ({ get }) => {
    const auth = get(authState);
    return auth?.profile?.loyaltyPoints || 0;
  },
  set: ({ set }, newValue) => {
    if (!(newValue instanceof DefaultValue)) {
      set(
        authState,
        (prev) =>
          ({
            ...prev,
            profile: {
              ...prev?.profile,
              loyaltyPoints: newValue,
            },
          } as ApplicationAuthState<any> | undefined),
      );
    }
  },
});

export const loyaltyPointsInvoicesStatsState = selector<
  | {
      isEnough: boolean;
      totalValueSpent: number;
      totalValueSpentTarget: number;
    }
  | undefined
>({
  key: 'loyaltyPointsInvoicesStats',
  get: ({ get }) => {
    const auth = get(authState);
    return auth?.profile?.loyaltyPointsInvoicesStats;
  },
  set: ({ set }, newValue) => {
    if (!(newValue instanceof DefaultValue)) {
      set(
        authState,
        (prev) =>
          ({
            ...prev,
            profile: {
              ...prev?.profile,
              loyaltyPointsInvoicesStats: newValue,
            },
          } as ApplicationAuthState<any> | undefined),
      );
    }
  },
});

/**
 * useApiClient
 */
export function useApiClient(): ApiClient {
  const auth = useRecoilValue(authState);
  return createApiClient({ accessToken: auth?.accessToken });
}
